import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  parseRepriceConditions,
  type RepriceLastRun,
  type RepriceParams,
} from '@/lib/repriceRules';

// Reprice automation rules manager. Reprice rules are the subset of
// `automation_rules` with trigger='reprice' — the daily repriceAgent edge
// function (supabase/functions/repriceAgent/index.ts) keys off exactly that
// trigger + enabled=true and reads their `conditions` jsonb. RLS scopes every
// query to the current user, so we never filter by user_id client-side.

export const REPRICE_RULES_QUERY_KEY = ['automation_rules', 'reprice'] as const;
export const ENABLED_AUTOMATION_COUNT_QUERY_KEY = ['automation_rules', 'enabled_count'] as const;

/**
 * Number of the user's automation rules that are switched on. Backs the
 * header's read-only Automations chip (fl-8q6), which replaced a cosmetic
 * Copilot/Autonomous toggle that changed nothing about what agents do.
 */
export function useEnabledAutomationCount() {
  return useQuery<number>({
    queryKey: ENABLED_AUTOMATION_COUNT_QUERY_KEY,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('automation_rules')
        .select('id', { count: 'exact', head: true })
        .eq('enabled', true);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60_000,
  });
}
export const REPRICE_LAST_RUN_QUERY_KEY = ['agent_actions', 'repriceAgent', 'last_run'] as const;

export interface RepriceRule {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  created_at: string;
  /** Parsed, default-filled parameters derived from the rule's `conditions`. */
  params: RepriceParams;
}

interface RepriceRuleRow {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  conditions: unknown;
  created_at: string;
}

/**
 * Fetch the current user's reprice rules with their real parameters parsed
 * out of `conditions`. Ordered newest-first to match the rest of the
 * Automation page.
 */
export function useRepriceRules() {
  return useQuery<RepriceRule[]>({
    queryKey: REPRICE_RULES_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('automation_rules')
        .select('id, name, description, enabled, conditions, created_at')
        .eq('trigger', 'reprice')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return ((data ?? []) as RepriceRuleRow[]).map((row) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        enabled: row.enabled,
        created_at: row.created_at,
        params: parseRepriceConditions(row.conditions),
      }));
    },
  });
}

/**
 * Toggle a rule's `enabled` flag and persist it. Invalidates both the
 * reprice-rules list and the shared automation_rules query used by the main
 * Automation page so the two views never drift.
 */
export function useToggleAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from('automation_rules')
        .update({ enabled, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPRICE_RULES_QUERY_KEY });
      // The main Automation page keys its list off ['automation_rules'].
      queryClient.invalidateQueries({ queryKey: ['automation_rules'] });
    },
  });
}

interface RepriceActionRow {
  created_at: string;
}

/**
 * Summarize the repriceAgent's most recent run for the current user.
 *
 * The agent logs one `agent_actions` row per item it reprices, all sharing a
 * timestamp from the same cron pass (`agent_name='repriceAgent'`). We treat
 * the newest row's date as "last run" and count rows from that same date as
 * "items repriced". Returns `null` when the agent has never run.
 */
export function useRepriceLastRun() {
  return useQuery<RepriceLastRun | null>({
    queryKey: REPRICE_LAST_RUN_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_actions')
        .select('created_at')
        .eq('agent_name', 'repriceAgent')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      const rows = (data ?? []) as RepriceActionRow[];
      const newest = rows[0];
      if (!newest) return null;
      const ranAt = newest.created_at;
      // Count rows from the same calendar run (same UTC day) as the newest.
      const runDay = ranAt.slice(0, 10);
      const itemsRepriced = rows.filter((r) => r.created_at.slice(0, 10) === runDay).length;
      return { ranAt, itemsRepriced };
    },
  });
}
